import { useParams } from 'react-router-dom'
import { buildErrorMessage, documentationRootGuid } from '@constants'
import { AxiosError } from 'axios'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getProjectFilesTemplates } from '@queries'

interface Options {
  /**
   * Reload the templates whenever the calling component mounts. Only the
   * document manager page sets this: it loads the templates in the background
   * when opened, and every other caller (e.g. the add-template dialog) reads
   * that cached result without refetching.
   */
  loadOnMount?: boolean
}

const useAvailableTemplates = ({ loadOnMount = false }: Options = {}) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  return useQuery<Record<string, TemplatesResult>, AxiosError>({
    queryKey: getProjectFilesTemplates.getKey(projectId, documentationRootGuid),
    queryFn: () => getProjectFilesTemplates.request(projectId, documentationRootGuid),
    // Kept while the document manager is open (changes invalidate them), dropped
    // as soon as it is left so every visit loads them fresh
    staleTime: Infinity,
    cacheTime: 0,
    refetchOnMount: loadOnMount ? 'always' : true,
    refetchOnWindowFocus: false,
    // Only the loading caller reports errors, so a failure isn't shown once per caller
    onError: loadOnMount
      ? error =>
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Vorlagen'), {
            variant: 'error',
          })
      : undefined,
  })
}

export default useAvailableTemplates
